/**
 * AI Savings Finder — Owner-dashboard tile.
 * =========================================
 * Surfaces REAL supplier-spend savings derived from the tenant's connected
 * accounting ledger (Xero / MYOB / QuickBooks) + a Claude plain-English
 * "act on this first" narrative. Requires accounting connected as the bare
 * minimum; when it isn't, the tile tells the owner exactly what to connect
 * rather than showing empty/fabricated numbers.
 *
 * Visibility is controlled by the `ai-savings-finder` dashboard widget toggle
 * (Dashboard Customization → permissions), so it only renders when enabled.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, ShieldCheck, PiggyBank, CheckCircle2, Loader2, Plug } from 'lucide-react';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';

const fmtAud = (v) => {
  const n = Number(v || 0);
  return `$${n.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
};

const Shell = ({ ring, children, testId }) => (
  <div
    className={`crystal-glass-dark grain-overlay rounded-3xl p-7 relative overflow-hidden ring-1 ${ring}`}
    data-testid={testId}
  >
    <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-gradient-radial from-emerald-400/25 to-transparent blur-2xl pointer-events-none" />
    <div className="relative">{children}</div>
  </div>
);

export default function AISavingsFinderTile() {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/api/insights/savings-finder`);
        if (!res.ok) { if (alive) setState({ loading: false, data: null }); return; }
        const data = await res.json();
        if (alive) setState({ loading: false, data });
      } catch {
        if (alive) setState({ loading: false, data: null });
      }
    })();
    return () => { alive = false; };
  }, []);

  if (state.loading || !state.data) return null;

  const d = state.data;

  const Header = ({ icon: Icon, tint, label, title }) => (
    <div className="flex items-start gap-3 mb-4">
      <div className={`p-2.5 rounded-xl ${tint}`}>
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-300 font-semibold mb-1">{label}</div>
        <h3 className="font-corvana-display text-xl font-light text-white tracking-tight leading-snug">{title}</h3>
      </div>
    </div>
  );

  // ── State: accounting not connected (bare minimum) ──
  if (d.state === 'no_accounting') {
    return (
      <Shell ring="ring-sky-400/15" testId="savings-finder-no-accounting">
        <Header icon={Plug} tint="bg-sky-400/15 text-sky-300" label="AI Savings Finder" title="Connect your accounting to find savings" />
        <p className="text-sm text-slate-300/80 leading-relaxed mb-4">{d.message}</p>
        <Link to={d.needs?.[0]?.connect_url || '/app/integrations/accounting'}>
          <button
            className="px-4 py-2 rounded-xl text-sm font-medium bg-sky-400/15 hover:bg-sky-400/25 text-sky-200 border border-sky-400/30 inline-flex items-center gap-2 transition-colors"
            data-testid="savings-finder-connect-accounting-btn"
          >
            Connect Xero / MYOB / QuickBooks <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </Link>
      </Shell>
    );
  }

  // ── State: needs vendor-spend consent ──
  if (d.state === 'consent_required') {
    return (
      <Shell ring="ring-emerald-400/15" testId="savings-finder-consent">
        <Header icon={ShieldCheck} tint="bg-emerald-400/15 text-emerald-300" label="AI Savings Finder" title="Unlock savings hiding in your bills" />
        <p className="text-sm text-slate-300/80 leading-relaxed mb-4">{d.message}</p>
        <Link to={d.consent_url || '/app/data-permissions'}>
          <button
            className="px-4 py-2 rounded-xl text-sm font-medium bg-emerald-400/15 hover:bg-emerald-400/25 text-emerald-200 border border-emerald-400/30 inline-flex items-center gap-2 transition-colors"
            data-testid="savings-finder-enable-scan-btn"
          >
            Enable vendor-spend scan <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </Link>
      </Shell>
    );
  }

  // ── State: connected + scanned, no leaks ──
  if (d.state === 'no_opportunities') {
    return (
      <Shell ring="ring-emerald-400/15" testId="savings-finder-clear">
        <Header icon={CheckCircle2} tint="bg-emerald-400/15 text-emerald-300" label="AI Savings Finder" title="No obvious savings leaks right now" />
        <p className="text-sm text-slate-300/80 leading-relaxed">{d.message}</p>
      </Shell>
    );
  }

  // ── State: ok — real savings ──
  const opps = d.opportunities || [];
  return (
    <Shell ring="ring-emerald-400/20" testId="savings-finder-tile">
      <Header icon={PiggyBank} tint="bg-emerald-400/15 text-emerald-300" label="AI Savings Finder" title="Potential savings in your supplier spend" />

      <div className="flex items-end gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-4xl font-bold text-emerald-300 tracking-tight" data-testid="savings-finder-monthly">
            {fmtAud(d.total_estimated_monthly_aud)}<span className="text-lg text-emerald-300/70 font-normal">/mo</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            ≈ {fmtAud(d.total_estimated_annual_aud)}/yr if optimised
          </div>
        </div>
        {typeof d.peer_percentile === 'number' && (
          <div className="ml-auto text-right" data-testid="savings-finder-percentile">
            <div className="text-2xl font-bold text-white">{Math.round(d.peer_percentile)}<span className="text-sm text-slate-400">th</span></div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400">percentile vs peers</div>
          </div>
        )}
      </div>

      {d.narrative && (
        <div className="flex items-start gap-2 mb-5 p-3.5 rounded-xl bg-white/5 border border-white/10" data-testid="savings-finder-narrative">
          <Sparkles className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-200 leading-relaxed">{d.narrative}</p>
        </div>
      )}

      <div className="space-y-2">
        {opps.map((o) => (
          <div
            key={o.integration_id}
            className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            data-testid={`savings-finder-opp-${o.integration_id}`}
          >
            <div className="min-w-0">
              <div className="font-medium text-sm text-white truncate">{o.label}</div>
              <div className="text-xs text-slate-400 truncate font-mono">{o.matched_vendor}</div>
            </div>
            <div className="text-sm font-semibold text-emerald-300 whitespace-nowrap font-mono">
              {fmtAud(o.estimated_monthly_aud)}/mo
            </div>
          </div>
        ))}
      </div>

      {d.sources_scanned?.length > 0 && (
        <div className="mt-4 text-[10px] text-white/40 text-right font-mono uppercase tracking-[0.14em]" data-testid="savings-finder-sources">
          Scanned · {d.sources_scanned.join(' · ')}
        </div>
      )}
    </Shell>
  );
}
